import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import Lenis from "lenis";
import { ArrowUpRight, ChevronRight, Menu, X, Globe2, Cog, Truck, Package, Wrench, Factory, Mail, MapPin } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_2cef5004-48ea-4a19-aa58-ac499f860eb6/artifacts/6b2v8w3x_stx-logo.png";

// STX Supplier & Training Expo banner (client-supplied)
const HERO_BANNER =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_page-maker-862/artifacts/34ktur1g_image.png";

// Editorial / automotive imagery (Unsplash — free to use)
const CARD_IMG_1 =
  "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=1200&q=80";
const CARD_IMG_2 =
  "https://images.unsplash.com/photo-1565043666747-69f6646db940?auto=format&fit=crop&w=1200&q=80";
const CARD_IMG_3 =
  "https://images.unsplash.com/photo-1567789884554-0b844b597180?auto=format&fit=crop&w=1200&q=80";
const INNOVATION_IMG =
  "https://images.unsplash.com/photo-1518709268805-4e9042af2176?auto=format&fit=crop&w=1600&q=80";
const REACH_IMG =
  "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1600&q=80";
const CAREER_IMG =
  "https://images.unsplash.com/photo-1504222490345-c075b6008014?auto=format&fit=crop&w=1600&q=80";

const NAV = [
  { label: "Company", href: "#company" },
  { label: "Solutions", href: "#solutions" },
  { label: "Innovation", href: "#innovation" },
  { label: "Global Reach", href: "#reach" },
  { label: "Sectors", href: "#sectors" },
  { label: "Contact", href: "#contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      className={`site-header ${scrolled ? "is-scrolled" : ""}`}
      data-testid="site-header"
    >
      <a href="#top" className="brand" data-testid="brand-link">
        <img src={LOGO_URL} alt="STX" className="brand-logo" />
        <span className="brand-name">
          STX Industrial Trading <em>L.L.C</em>
        </span>
      </a>

      <nav className="nav" aria-label="Primary" data-testid="primary-nav">
        {NAV.map((n) => (
          <a key={n.href} href={n.href} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
            {n.label}
          </a>
        ))}
      </nav>

      <div className="header-cta">
        <a href="#contact" className="btn btn-solid" data-testid="header-contact">
          Get in touch <ArrowUpRight size={14} />
        </a>
      </div>

      <button
        className="hamburger"
        aria-label="Menu"
        onClick={() => setOpen((v) => !v)}
        data-testid="mobile-menu-toggle"
      >
        {open ? <X size={22} /> : <Menu size={22} />}
      </button>

      {open && (
        <div className="mobile-nav" data-testid="mobile-nav">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} onClick={() => setOpen(false)}>
              {n.label}
              <ChevronRight size={16} />
            </a>
          ))}
          <a href="#contact" className="btn btn-solid" onClick={() => setOpen(false)}>
            Get in touch <ArrowUpRight size={14} />
          </a>
        </div>
      )}
    </header>
  );
};

/* ============ Motion helpers ============ */
const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
};

const Reveal = ({ children, delay = 0, y = 24, className, ...rest }) => {
  const rm = useReducedMotion();
  if (rm) return <div className={className} {...rest}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

// Word-by-word rise reveal for headline lines
const RevealLine = ({ text, delay = 0, className }) => {
  const rm = useReducedMotion();
  const words = text.split(" ");
  if (rm) return <span className={className}>{text}</span>;
  return (
    <span className={`reveal-line ${className || ""}`}>
      {words.map((w, i) => (
        <span key={i} className="reveal-word">
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{
              duration: 0.95,
              ease: [0.22, 1, 0.36, 1],
              delay: delay + i * 0.06,
            }}
          >
            {w}
          </motion.span>
          {i < words.length - 1 && "\u00A0"}
        </span>
      ))}
    </span>
  );
};

const Hero = () => {
  const rm = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.3]);

  return (
    <section className="hero" id="top" ref={ref} data-testid="hero-section">
      <div className="hero-bg" aria-hidden>
        <div className="hero-grid-pattern" />
        <div className="hero-glow hero-glow-a" />
        <div className="hero-glow hero-glow-b" />
      </div>

      <motion.div className="hero-inner" style={rm ? {} : { opacity }}>
        <motion.div
          className="eyebrow"
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <span className="dot" />
          <span className="eyebrow-tag">Supplier &amp; Training Expo · 2026</span>
          <span className="eyebrow-sep" />
          <span>Automotive Components &amp; Industrial Trading</span>
        </motion.div>

        <h1 data-testid="hero-title" className="hero-title">
          <span className="line-wrap"><RevealLine text="Lead with" delay={0.15} /></span>
          <span className="line-wrap"><RevealLine text="innovation." delay={0.35} className="accent" /></span>
        </h1>

        <motion.p
          className="hero-lede"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
        >
          STX Industrial Trading L.L.C connects industrial demand with dependable
          supply — automotive components, purchased parts, consumables and B2B
          distribution across borders.
        </motion.p>

        <motion.div
          className="hero-actions"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
        >
          <a className="btn btn-solid gold" href="#solutions" data-testid="hero-cta-solutions">
            Explore our solutions <ArrowUpRight size={14} />
          </a>
          <a className="btn btn-outline dark" href="#company" data-testid="hero-cta-about">
            About STX
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        className="hero-banner-wrap"
        style={rm ? {} : { y, scale }}
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="hero-banner-frame">
          <img src={HERO_BANNER} alt="STX 2026 · Supplier & Training Expo — Lead with Innovation" />
          <div className="hero-banner-shine" />
          <div className="hero-banner-corner tl" />
          <div className="hero-banner-corner tr" />
          <div className="hero-banner-corner bl" />
          <div className="hero-banner-corner br" />
        </div>
        <div className="hero-banner-caption">
          <span>STX 2026 · Supplier &amp; Training Expo</span>
          <span className="dot-sep">/</span>
          <span>Washington, D.C.</span>
        </div>
      </motion.div>

      <div className="hero-strip" data-testid="hero-strip">
        <Reveal delay={0.05}><b>1600+</b><span>Sourcing partners</span></Reveal>
        <Reveal delay={0.12}><b>12</b><span>Core categories</span></Reveal>
        <Reveal delay={0.19}><b>B2B</b><span>Trading focus</span></Reveal>
        <Reveal delay={0.26}><b>24/7</b><span>Procurement window</span></Reveal>
      </div>

      <motion.div
        className="scroll-hint"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        aria-hidden
      >
        <span>Scroll</span>
        <span className="scroll-line" />
      </motion.div>
    </section>
  );
};

const FeaturedCards = () => {
  const items = [
    {
      tag: "Update",
      img: CARD_IMG_1,
      title: "Expanding automotive supplier network across MENA and Europe",
      cta: "Read more",
    },
    {
      tag: "Insight",
      img: CARD_IMG_2,
      title: "How professional B2B procurement removes friction from supply chains",
      cta: "Read insight",
    },
    {
      tag: "Program",
      img: CARD_IMG_3,
      title: "Consumables & workshop materials — a dependable repeat-demand stream",
      cta: "See program",
    },
  ];
  return (
    <section className="featured" data-testid="featured-section">
      <Reveal className="featured-head" y={20}>
        <h2>What&apos;s driving STX</h2>
        <a href="#solutions" className="link-inline" data-testid="featured-all">
          See all solutions <ArrowUpRight size={14} />
        </a>
      </Reveal>
      <div className="featured-grid">
        {items.map((it, i) => (
          <Reveal
            key={i}
            delay={i * 0.1}
            y={30}
            className="featured-card-wrap"
          >
            <a href="#solutions" className="featured-card" data-testid={`featured-card-${i}`}>
              <div className="featured-img">
                <img src={it.img} alt="" />
              </div>
              <div className="featured-body">
                <span className="tag">{it.tag}</span>
                <h3>{it.title}</h3>
                <span className="link-inline">{it.cta} <ArrowUpRight size={14} /></span>
              </div>
            </a>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const About = () => (
  <section id="company" className="about" data-testid="about-section">
    <Reveal className="about-copy" y={30}>
      <div className="section-label">About STX</div>
      <h2>
        A focused B2B trading partner for the automotive and industrial{" "}
        <em>supply chain.</em>
      </h2>
      <p>
        STX Industrial Trading L.L.C combines commercial discipline with
        procurement expertise. We create efficient sourcing routes for
        automotive suppliers, manufacturers and industrial buyers who need
        speed, transparency and a professional counterpart on the other side of
        the table.
      </p>
      <a className="link-inline strong" href="#solutions">
        Learn what we do <ArrowUpRight size={14} />
      </a>
    </Reveal>
    <div className="stats" data-testid="stats-grid">
      {[
        ["12", "Core product categories"],
        ["B2B", "Trading & distribution model"],
        ["Global", "Sourcing & procurement reach"],
        ["100%", "Focus on execution & reliability"],
      ].map(([n, t], i) => (
        <Reveal key={n} className="stat" delay={i * 0.08} y={30}>
          <b>{n}</b>
          <span>{t}</span>
        </Reveal>
      ))}
    </div>
  </section>
);

const Solutions = () => {
  const items = [
    { icon: Cog, n: "01", title: "Automotive Components", text: "Supplier parts, assemblies, accessories and replacement components for automotive applications." },
    { icon: Wrench, n: "02", title: "Industrial Purchased Parts", text: "Commercial sourcing of technical purchased parts, operational materials and repeat-demand items." },
    { icon: Package, n: "03", title: "Consumables & Accessories", text: "Packaging, workshop and production consumables, as well as tailored accessory solutions." },
    { icon: Truck, n: "04", title: "Trading & Distribution", text: "Flexible B2B trade structures for established supply relationships and new procurement opportunities." },
  ];
  return (
    <section id="solutions" className="solutions" data-testid="solutions-section">
      <div className="solutions-head">
        <div>
          <div className="section-label">Solutions</div>
          <h2>What we represent</h2>
        </div>
        <p>
          Clear supply channels, commercially minded sourcing and a strong
          focus on dependable execution across four core categories.
        </p>
      </div>
      <div className="solutions-grid">
        {items.map(({ icon: Icon, n, title, text }, i) => (
          <Reveal
            key={n}
            delay={i * 0.08}
            y={26}
            className="solution-card"
            data-testid={`solution-card-${n}`}
          >
            <div className="solution-top">
              <span className="solution-n">{n}</span>
              <Icon size={22} strokeWidth={1.5} />
            </div>
            <h3>{title}</h3>
            <p>{text}</p>
            <span className="link-inline strong">Talk to us <ArrowUpRight size={14} /></span>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Innovation = () => (
  <section id="innovation" className="innovation" data-testid="innovation-section">
    <div className="innovation-media">
      <img src={INNOVATION_IMG} alt="Industrial precision" />
    </div>
    <div className="innovation-copy">
      <div className="section-label light">The STX Approach</div>
      <h2>
        Professional procurement,<br /><em>without unnecessary noise.</em>
      </h2>
      <p>
        We work where quality expectations, commercial clarity and delivery
        reliability matter. Our job is to remove friction between industrial
        buyers and dependable supply — nothing more, nothing less.
      </p>
      <ul className="approach-list">
        <li><b>Focused</b><span>on commercially viable solutions</span></li>
        <li><b>Flexible</b><span>in sourcing and trading structures</span></li>
        <li><b>Reliable</b><span>in communication and execution</span></li>
      </ul>
    </div>
  </section>
);

const Reach = () => (
  <section id="reach" className="reach" data-testid="reach-section">
    <div className="reach-copy">
      <div className="section-label">Global Reach</div>
      <h2>Built for cross-border industrial trade.</h2>
      <p>
        Our sourcing and trading structures are designed to move — across
        markets, through customs corridors and into the hands of the
        manufacturers, workshops and distributors that keep industry running.
      </p>
      <a className="btn btn-outline dark" href="#contact" data-testid="reach-cta">
        Discuss a sourcing route <ArrowUpRight size={14} />
      </a>
    </div>
    <div className="reach-visual">
      <img src={REACH_IMG} alt="Global logistics" />
      <div className="reach-badge">
        <Globe2 size={16} />
        <span>Automotive · Industrial · Trading</span>
      </div>
    </div>
  </section>
);

const Sectors = () => (
  <section id="sectors" className="sectors" data-testid="sectors-section">
    <div className="sectors-head">
      <div className="section-label">Sectors we serve</div>
      <h2>Automotive discipline. <em>Industrial range.</em></h2>
    </div>
    <div className="sectors-list">
      {[
        ["01", "Automotive supply chain", "OEM & Tier-N suppliers, aftermarket, workshop trade."],
        ["02", "Manufacturing & production", "Repeat-demand items, technical purchased parts, operational materials."],
        ["03", "Packaging & operational materials", "Industrial consumables and workshop supplies."],
        ["04", "Technical trade & B2B distribution", "Cross-border structures for professional buyers and traders."],
      ].map(([n, title, text]) => (
        <div key={n} className="sector-row" data-testid={`sector-${n}`}>
          <span className="sector-n">{n}</span>
          <div className="sector-title">{title}</div>
          <div className="sector-text">{text}</div>
          <div className="sector-arrow"><ArrowUpRight size={18} /></div>
        </div>
      ))}
    </div>
  </section>
);

const Career = () => (
  <section className="career" data-testid="career-section">
    <div className="career-inner">
      <img src={CAREER_IMG} alt="Workshop" className="career-bg" />
      <div className="career-overlay" />
      <div className="career-content">
        <div className="section-label light">Partner with STX</div>
        <h2>Looking for a serious sourcing counterpart?</h2>
        <p>
          If you&apos;re an OEM buyer, workshop network, distributor or industrial
          manufacturer — we&apos;d be happy to hear where your supply chain hurts.
        </p>
        <a className="btn btn-solid gold" href="#contact" data-testid="career-cta">
          Start a conversation <ArrowUpRight size={14} />
        </a>
      </div>
    </div>
  </section>
);

const Contact = () => (
  <section id="contact" className="contact" data-testid="contact-section">
    <div className="contact-grid">
      <div>
        <div className="section-label">Contact</div>
        <h2>Let&apos;s talk sourcing.</h2>
        <p>
          For procurement enquiries, trading opportunities, supplier onboarding
          or general business questions — send us a note and we will get back
          to you.
        </p>
        <div className="contact-details" data-testid="contact-details">
          <a className="contact-detail" href="mailto:sales@stx-industrial.com" data-testid="contact-email">
            <span className="contact-ic"><Mail size={16} /></span>
            <span>
              <b>Email</b>
              sales@stx-industrial.com
            </span>
          </a>
          <div className="contact-detail" data-testid="contact-address">
            <span className="contact-ic"><MapPin size={16} /></span>
            <span>
              <b>Address</b>
              30 N Gould St Ste R,<br />Sheridan, WY 82801, USA
            </span>
          </div>
        </div>
      </div>
      <ContactForm />
    </div>
  </section>
);

const ContactForm = () => {
  const [sent, setSent] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 4500);
    e.currentTarget.reset();
  };
  return (
    <form className="contact-form" onSubmit={submit} data-testid="contact-form">
      <label>
        Name
        <input required name="name" placeholder="Your name" data-testid="input-name" />
      </label>
      <label>
        Company
        <input name="company" placeholder="Your company" data-testid="input-company" />
      </label>
      <label className="full">
        Email
        <input required type="email" name="email" placeholder="you@company.com" data-testid="input-email" />
      </label>
      <label className="full">
        Message
        <textarea required name="message" rows="5" placeholder="Tell us about your sourcing need" data-testid="input-message" />
      </label>
      <div className="form-actions full">
        <button className="btn btn-solid" type="submit" data-testid="submit-contact">
          Send message <ArrowUpRight size={14} />
        </button>
        {sent && <span className="form-sent" data-testid="form-sent">Thanks — we&apos;ll be in touch.</span>}
      </div>
    </form>
  );
};

const Footer = () => (
  <footer className="site-footer" data-testid="site-footer">
    <div className="footer-top">
      <div className="footer-brand">
        <img src={LOGO_URL} alt="STX" />
        <p>
          STX Industrial Trading L.L.C — Automotive components, industrial
          sourcing and B2B trading.
        </p>
      </div>
      <div className="footer-cols">
        <div>
          <h4>Company</h4>
          <a href="#company">About</a>
          <a href="#innovation">Approach</a>
          <a href="#reach">Global Reach</a>
        </div>
        <div>
          <h4>Solutions</h4>
          <a href="#solutions">Automotive Components</a>
          <a href="#solutions">Industrial Parts</a>
          <a href="#solutions">Consumables</a>
          <a href="#solutions">Trading &amp; Distribution</a>
        </div>
        <div>
          <h4>Sectors</h4>
          <a href="#sectors">Automotive supply chain</a>
          <a href="#sectors">Manufacturing</a>
          <a href="#sectors">Packaging</a>
          <a href="#sectors">B2B distribution</a>
        </div>
        <div>
          <h4>Get in touch</h4>
          <a href="mailto:sales@stx-industrial.com">sales@stx-industrial.com</a>
          <a href="#contact">Contact form</a>
          <span className="footer-address">
            30 N Gould St Ste R,<br />Sheridan, WY 82801, USA
          </span>
        </div>
      </div>
    </div>
    <div className="footer-bottom">
      <span>© {new Date().getFullYear()} STX Industrial Trading L.L.C</span>
      <span className="footer-group" data-testid="footer-group">Part of STX Group</span>
      <span>International B2B Trading</span>
    </div>
  </footer>
);

const Marquee = () => {
  const items = [
    "Automotive Components",
    "Industrial Purchased Parts",
    "Consumables & Accessories",
    "Trading & Distribution",
    "Cross-border Sourcing",
    "B2B Procurement",
    "OEM & Aftermarket",
  ];
  const row = [...items, ...items, ...items];
  return (
    <section className="marquee" aria-hidden data-testid="marquee-section">
      <div className="marquee-track">
        {row.map((t, i) => (
          <span key={i} className="marquee-item">
            {t}<span className="marquee-dot">◆</span>
          </span>
        ))}
      </div>
    </section>
  );
};

const Home = () => {
  useLenis();
  return (
    <div className="site" data-testid="stx-page">
      <Header />
      <main>
        <Hero />
        <Marquee />
        <FeaturedCards />
        <About />
        <Solutions />
        <Innovation />
        <Reach />
        <Sectors />
        <Career />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
